import React, { useState, useCallback } from 'react';
import { X, Send, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactModal = ({ isOpen, onClose }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not ready. Please try again.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Get reCAPTCHA v3 token
      const recaptchaToken = await executeRecaptcha('contact_form');
      
      await axios.post(`${API}/contact`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');

      // Reset form after showing success
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setIsSubmitted(false);
        onClose();
      }, 2500);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-4xl bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 animate-in zoom-in-95 duration-200 overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors"
          data-testid="contact-modal-close"
        >
          <X size={20} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left Side - Form */}
          <div className="p-8 md:p-10">
            <h2 className="text-2xl font-bold text-white mb-6">Partner with Us</h2>
            
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Thank You!</h4>
                <p className="text-slate-400">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="contact-name" className="text-slate-300 mb-2 block text-sm">
                    Name
                  </Label>
                  <Input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20 h-11"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-email" className="text-slate-300 mb-2 block text-sm">
                    Email
                  </Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20 h-11"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-message" className="text-slate-300 mb-2 block text-sm">
                    Your Message
                  </Label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    rows={4}
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-5 rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-400/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </span>
                  )}
                </Button>

                {/* reCAPTCHA disclaimer */}
                <p className="text-xs text-slate-500 text-center mt-2">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-400">Privacy Policy</a>{' '}
                  and{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-400">Terms of Service</a>{' '}
                  apply.
                </p>
              </form>
            )}
          </div>

          {/* Right Side - Info */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-850 p-8 md:p-10 flex flex-col justify-center border-l border-slate-800">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-amber-400 mb-3">
                  Interested in partnering with us?
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Share your details via the form or drop us an email below, and our team will reach out to you as soon as possible.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-700">
                <p className="text-slate-400 text-sm mb-3">Do reach out to our email</p>
                <a 
                  href="mailto:help@willkart.ai"
                  className="flex items-center gap-3 group"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className="text-white font-medium text-lg group-hover:text-amber-400 transition-colors">
                    help@willkart.ai
                  </span>
                </a>
              </div>

              {/* Decorative Element */}
              <div className="pt-6">
                <div className="flex items-center gap-3 text-slate-500 text-sm">
                  <div className="w-8 h-px bg-slate-700"></div>
                  <span>We typically respond within 24 hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
